import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { leaderboardApi } from '../services/leaderboard.api';
import type { LeaderboardScope, LeaderboardPeriod } from 'shared-types';

const LEADERBOARD_STALE_TIME = 30_000;

export const leaderboardQueryKeys = {
  all: ['leaderboard'] as const,
  userStats: (scope: LeaderboardScope, period: LeaderboardPeriod, country?: string) =>
    [...leaderboardQueryKeys.all, 'user-stats', scope, period, country] as const,
  entries: (scope: LeaderboardScope, period: LeaderboardPeriod, country?: string) =>
    [...leaderboardQueryKeys.all, 'entries', scope, period, country] as const
};

interface UseLeaderboardDataParams {
  scope: LeaderboardScope;
  period: LeaderboardPeriod;
  country?: string;
  enabled?: boolean;
}

export function useUserStats({ scope, period, country, enabled = true }: UseLeaderboardDataParams) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: leaderboardQueryKeys.userStats(scope, period, country),
    queryFn: () => leaderboardApi.getUserStats({ scope, period, country }),
    enabled: enabled && isAuthenticated,
    staleTime: LEADERBOARD_STALE_TIME
  });
}

export function useLeaderboardEntries({
  scope,
  period,
  country,
  enabled = true
}: UseLeaderboardDataParams) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: leaderboardQueryKeys.entries(scope, period, country),
    queryFn: () => leaderboardApi.getLeaderboard({ scope, period, country, limit: 50 }),
    enabled: enabled && isAuthenticated,
    staleTime: LEADERBOARD_STALE_TIME
  });
}
